import { Camera, Loader2, Upload, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Button, Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import { inventoryApi } from './inventoryApi.js';
import { getPresignedImageUploadUrl, uploadImageToS3 } from './inventoryOnboardingApi.js';

const ReturnMaintenance = () => {
  // Log screen view
  useScreenLogging('ReturnMaintenance', {
    component: 'ReturnMaintenance',
    screenType: 'return-maintenance',
  });

  const navigate = useNavigate();
  const { itemId } = useParams();
  
  const [inventoryItem, setInventoryItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  
  // Form fields
  const [maintenanceRemark, setMaintenanceRemark] = useState('');
  const [dateOfReturn, setDateOfReturn] = useState(null);
  const [maintenanceCharge, setMaintenanceCharge] = useState('');
  const [transportationCharges, setTransportationCharges] = useState('');
  const [safetyMeasures, setSafetyMeasures] = useState('');
  
  // Image upload
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isImageValid, setIsImageValid] = useState(true);
  
  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Preview mode
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (itemId) {
      fetchInventoryItem();
    }
  }, [itemId]);

  const fetchInventoryItem = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const result = await inventoryApi.getInventoryItem(itemId);

      if (result.success) {
        const itemData = result.data?.item || result.data;
        setInventoryItem(itemData);
      } else {
        setErrorMessage(result.message || 'Failed to fetch inventory item');
      }
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      setErrorMessage(error.message || 'Failed to fetch inventory item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setSubmitError(`Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`);
      setIsImageValid(false);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setSubmitError('Please select a valid image file.');
      setIsImageValid(false);
      return;
    }

    setSelectedImage(file);
    setIsImageValid(true);
    setSubmitError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Check if camera is available
  const checkCameraAvailability = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          available: false,
          error: 'Camera not supported in this browser',
        };
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      if (videoDevices.length === 0) {
        return { available: false, error: 'No camera found on this device' };
      }

      return { available: true, devices: videoDevices };
    } catch (err) {
      console.error('Error checking camera availability:', err);
      return { available: false, error: 'Unable to check camera availability' };
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        throw new Error(cameraCheck.error);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);

      let errorMessage = 'Unable to access camera. Please check permissions.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (err.message.includes('not supported')) {
        errorMessage = 'Camera not supported in this browser.';
      } else if (err.message.includes('No camera found')) {
        errorMessage = 'No camera found on this device.';
      }

      setCameraError(errorMessage);
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  // Capture photo
  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0);

      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      // Convert data URL to File object
      const response = await fetch(photoDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });

      setSelectedImage(file);
      setImagePreview(photoDataUrl);
      setIsImageValid(true);
      setSubmitError(null);

      stopCamera();
    } catch (err) {
      console.error('Error capturing photo:', err);
      setSubmitError('Failed to capture photo. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  // Handle camera capture button click
  const handleCameraCapture = () => {
    if (isCameraActive) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedImage) {
      setIsImageValid(false);
      setSubmitError('Please upload a maintenance photo');
      return;
    }
    
    if (!maintenanceRemark.trim() || !safetyMeasures.trim()) {
      setSubmitError('Please fill all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Upload image first
      let maintenanceImageUrl = null;
      try {
        const presignResponse = await getPresignedImageUploadUrl({
          filename: `maintenance_return_${Date.now()}.jpg`,
          content_type: 'image/jpeg',
          for_maintenance: true,
        });

        // Convert file to blob
        const imageBlob = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const arrayBuffer = reader.result;
            const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
            resolve(blob);
          };
          reader.onerror = reject;
          reader.readAsArrayBuffer(selectedImage);
        });

        await uploadImageToS3(presignResponse.post_data, imageBlob);
        maintenanceImageUrl = presignResponse.s3_url;
      } catch (imageError) {
        console.warn('Failed to upload maintenance return image:', imageError);
        // Continue without image
      }

      // Format date
      const actualReturnDateISO = dateOfReturn 
        ? new Date(dateOfReturn).toISOString()
        : new Date().toISOString();

      // Call completeMaintenance API
      const result = await inventoryApi.completeMaintenance({
        itemId: itemId,
        maintenanceRemarks: maintenanceRemark.trim(),
        actualReturnDate: actualReturnDateISO,
        maintenanceCharges: maintenanceCharge && maintenanceCharge !== '' ? parseFloat(maintenanceCharge) : null,
        transportationCharges: transportationCharges && transportationCharges !== '' ? parseFloat(transportationCharges) : null,
        safetyMeasures: safetyMeasures.trim(),
        maintenanceImageUrl: maintenanceImageUrl,
      });

      if (result.success) {
        // Navigate back to item detail page
        navigate(`/inventory-item-detail/${itemId}`);
      } else {
        setSubmitError(result.message || 'Failed to complete maintenance');
      }
    } catch (error) {
      console.error('Error completing maintenance:', error);
      setSubmitError(error.message || 'Failed to complete maintenance');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-4xl mx-auto h-full flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading inventory item...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Return Maintenance
            </h1>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <X className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h3 className={typography.h3} style={{ color: '#DC2626', marginBottom: '0.5rem' }}>
                Error loading inventory item
              </h3>
              <p className="text-red-600 mb-4">{errorMessage}</p>
              <div className="flex gap-4 justify-center">
                <Button
                  type="button"
                  onClick={() => navigate(`/inventory-item-detail/${itemId}`)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  Go Back
                </Button>
                <PrimaryButton
                  type="button"
                  onClick={fetchInventoryItem}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Retry
                </PrimaryButton>
              </div>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1
            className={typography.h1}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            Return Maintenance
          </h1>
        </div>

        {/* Maintenance Information Section */}
        <div className="mb-6">
          <h2
            className={typography.h2}
            style={{ color: '#000000', marginBottom: '1rem' }}
          >
            Maintenance Information
              </h2>
          </div>

        {/* Maintenance Form */}
        <Card.Light className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Maintenance Photo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Maintenance Photo <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col items-center gap-4">
                  {/* Image Preview */}
                  <div className="relative">
                    {imagePreview ? (
                      <div className={`w-32 h-32 rounded-full border-2 overflow-hidden ${
                        isImageValid ? 'border-gray-300' : 'border-red-500'
                      }`}>
                        <img
                          src={imagePreview}
                          alt="Maintenance preview"
                          className="w-full h-full object-cover"
                        />
                        {!isPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setImagePreview(null);
                              setSelectedImage(null);
                              setIsImageValid(true);
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className={`w-32 h-32 rounded-full border-2 border-dashed flex items-center justify-center ${
                        isImageValid ? 'border-gray-300 bg-gray-50' : 'border-red-500 bg-red-50'
                      }`}>
                        <Camera className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Upload Buttons */}
                  {!isPreview && (
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        style={{ color: '#ffffff' }}
                      >
                        <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                        <span style={{ color: '#ffffff' }}>Upload Image</span>
                      </Button>
                      <Button
                        type="button"
                        onClick={handleCameraCapture}
                        className={`flex items-center gap-2 ${
                          isCameraActive
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                        style={{ color: '#ffffff' }}
                      >
                        <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
                        <span style={{ color: '#ffffff' }}>Take Photo</span>
                      </Button>
                    </div>
                  )}

                  {/* Hidden Canvas for Photo Capture */}
                  <canvas
                    ref={canvasRef}
                    className="hidden"
                    width="640"
                    height="480"
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isPreview}
                  />
                </div>

                {/* Camera Preview */}
                {showCameraPreview && (
                  <div className="w-full mt-6">
                    <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                      <div className="text-center mb-3">
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                          Camera Preview
                        </h3>
                        {cameraError && (
                          <p className="text-red-600 text-sm mb-2">
                            {cameraError}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-center gap-4">
                        <div className="relative">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                          />

                          <div className="flex justify-center gap-3 mt-3">
                            <Button
                              type="button"
                              onClick={takePhoto}
                              disabled={isCapturing}
                              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                              style={{ color: '#ffffff' }}
                            >
                              {isCapturing ? 'Capturing...' : 'Capture Photo'}
                            </Button>

                            <Button
                              type="button"
                              onClick={stopCamera}
                              className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                              style={{ color: '#ffffff' }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Maintenance Remark */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                   Remark after maintenance <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={maintenanceRemark}
                  onChange={(e) => setMaintenanceRemark(e.target.value)}
                  placeholder="Enter remark after maintenance"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={isPreview}
                />
              </div>

              {/* Date of Return */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date of Return
                </label>
                <input
                  type="date"
                  value={dateOfReturn ? new Date(dateOfReturn).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setDateOfReturn(new Date(e.target.value));
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  disabled={isPreview}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Maintenance Charge */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                 Charges / cost for maintenence<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={maintenanceCharge}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMaintenanceCharge(val === '' ? '' : (isNaN(parseFloat(val)) ? '' : val));
                  }}
                  placeholder="Enter amount"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={isPreview}
                />
              </div>

              {/* Transportation Charges */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Transportation Charges (To-from) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={transportationCharges}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTransportationCharges(val === '' ? '' : (isNaN(parseFloat(val)) ? '' : val));
                  }}
                  placeholder="Enter amount"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={isPreview}
                />
              </div>

              {/* Safety Measures */}
              <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Safety Measures <span className="text-red-500">*</span>
              </label>
              <textarea
                  value={safetyMeasures}
                  onChange={(e) => setSafetyMeasures(e.target.value)}
                  placeholder="Enter safety measures"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={4}
                required
                  disabled={isPreview}
              />
            </div>

            {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                {isPreview ? (
                  <>
              <Button
                type="button"
                      onClick={() => setIsPreview(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                      Edit
              </Button>
              <PrimaryButton
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      disabled={isSubmitting}
                      style={{ color: '#ffffff' }}
              >
                {isSubmitting ? (
                  <>
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" style={{ color: '#ffffff' }} />
                          <span style={{ color: '#ffffff' }}>Submitting...</span>
                        </>
                      ) : (
                        <span style={{ color: '#ffffff' }}>Submit</span>
                      )}
                    </PrimaryButton>
                  </>
                ) : (
                  <Button
                    type="button"
                    onClick={() => setIsPreview(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    style={{ color: '#ffffff' }}
                  >
                    <span style={{ color: '#ffffff' }}>Check Preview</span>
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Card.Light>
      </div>
    </div>
  );
};

export default ReturnMaintenance;
